import * as THREE from 'three';

/**
 * Interface for 2D points used in polygon calculations
 */
export interface Point2D {
  x: number;
  z: number;
}

/**
 * Checks if a point is inside a polygon using ray casting algorithm
 * @param x X coordinate of the point
 * @param z Z coordinate of the point (using z instead of y for 3D space)
 * @param polygon Array of vertices that make up the polygon
 * @returns true if point is inside polygon, false otherwise
 */
export const pointInPolygon = (
  x: number,
  z: number,
  polygon: Point2D[]
): boolean => {
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i].x;
    const zi = polygon[i].z;
    const xj = polygon[j].x;
    const zj = polygon[j].z;

    const intersect =
      zi > z !== zj > z && x < ((xj - xi) * (z - zi)) / (zj - zi) + xi;

    if (intersect) inside = !inside;
  }
  return inside;
};

/**
 * Checks if two polygons intersect
 * @param poly1 First polygon as array of Point2D
 * @param poly2 Second polygon as array of Point2D
 * @returns true if polygons intersect, false otherwise
 */
export const polygonPolygonIntersect = (
  poly1: Point2D[],
  poly2: Point2D[]
): boolean => {
  // First check if bounding boxes overlap (optimization)
  const bbox1 = getBoundingBox2D(poly1);
  const bbox2 = getBoundingBox2D(poly2);

  if (!boundingBoxesIntersect(bbox1, bbox2)) {
    return false;
  }

  // Check if any point of poly1 is inside poly2
  for (const point of poly1) {
    if (pointInPolygon(point.x, point.z, poly2)) {
      return true;
    }
  }

  // Check if any point of poly2 is inside poly1
  for (const point of poly2) {
    if (pointInPolygon(point.x, point.z, poly1)) {
      return true;
    }
  }

  // Check if any line segments intersect
  for (let i = 0, j = poly1.length - 1; i < poly1.length; j = i++) {
    for (let k = 0, l = poly2.length - 1; k < poly2.length; l = k++) {
      if (
        lineSegmentsIntersect(
          poly1[j].x,
          poly1[j].z,
          poly1[i].x,
          poly1[i].z,
          poly2[l].x,
          poly2[l].z,
          poly2[k].x,
          poly2[k].z
        )
      ) {
        return true;
      }
    }
  }

  return false;
};

/**
 * Calculates 2D bounding box for a set of points
 */
export const getBoundingBox2D = (points: Point2D[]) => {
  let minX = Infinity;
  let maxX = -Infinity;
  let minZ = Infinity;
  let maxZ = -Infinity;

  for (const point of points) {
    minX = Math.min(minX, point.x);
    maxX = Math.max(maxX, point.x);
    minZ = Math.min(minZ, point.z);
    maxZ = Math.max(maxZ, point.z);
  }

  return { minX, maxX, minZ, maxZ };
};

/**
 * Checks if two 2D bounding boxes intersect
 */
export const boundingBoxesIntersect = (
  box1: { minX: number; maxX: number; minZ: number; maxZ: number },
  box2: { minX: number; maxX: number; minZ: number; maxZ: number }
) => {
  return (
    box1.minX <= box2.maxX &&
    box1.maxX >= box2.minX &&
    box1.minZ <= box2.maxZ &&
    box1.maxZ >= box2.minZ
  );
};

/**
 * Checks if two line segments intersect
 */
export const lineSegmentsIntersect = (
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  x3: number,
  y3: number,
  x4: number,
  y4: number
): boolean => {
  // Calculate the direction of the lines
  const uA =
    ((x4 - x3) * (y1 - y3) - (y4 - y3) * (x1 - x3)) /
    ((y4 - y3) * (x2 - x1) - (x4 - x3) * (y2 - y1));
  const uB =
    ((x2 - x1) * (y1 - y3) - (y2 - y1) * (x1 - x3)) /
    ((y4 - y3) * (x2 - x1) - (x4 - x3) * (y2 - y1));

  // If uA and uB are between 0-1, lines are colliding
  return uA >= 0 && uA <= 1 && uB >= 0 && uB <= 1;
};

/**
 * Gets corners of an object in the XZ plane
 * @param object Three.js mesh object
 * @param customBox Optional custom bounding box to use instead of the object's default
 * @returns Array of corner points
 */
export const getCorners = (
  object: THREE.Mesh,
  customBox?: THREE.Box3
): Point2D[] => {
  // Get the bounding box, using the custom one if provided
  const boundingBox = customBox
    ? customBox.clone().translate(object.position)
    : new THREE.Box3().setFromObject(object);

  // For rotated objects, we need to calculate corners differently
  if (object.rotation.y !== 0) {
    // Get the object's dimensions
    const size = new THREE.Vector3();
    boundingBox.getSize(size);
    const halfSize = size.clone().multiplyScalar(0.5);

    // Create corners in local space (before rotation)
    const localCorners = [
      new THREE.Vector3(-halfSize.x, 0, -halfSize.z),
      new THREE.Vector3(halfSize.x, 0, -halfSize.z),
      new THREE.Vector3(halfSize.x, 0, halfSize.z),
      new THREE.Vector3(-halfSize.x, 0, halfSize.z),
    ];

    // Create a matrix to represent the object's position and rotation
    const matrix = new THREE.Matrix4();
    matrix.makeRotationY(object.rotation.y);
    matrix.setPosition(object.position);

    // Apply the matrix to transform corners to world space with correct rotation
    const worldCorners = localCorners.map((corner) => {
      const worldCorner = corner.clone();
      worldCorner.applyMatrix4(matrix);
      return { x: worldCorner.x, z: worldCorner.z };
    });

    return worldCorners;
  } else {
    // For non-rotated objects, use the original implementation
    // Calculate half sizes
    const size = new THREE.Vector3();
    boundingBox.getSize(size);
    const halfSize = size.clone().multiplyScalar(0.5);

    // Create corners in local space
    const corners = [
      new THREE.Vector3(-halfSize.x, 0, -halfSize.z),
      new THREE.Vector3(halfSize.x, 0, -halfSize.z),
      new THREE.Vector3(halfSize.x, 0, halfSize.z),
      new THREE.Vector3(-halfSize.x, 0, halfSize.z),
    ];

    // Transform corners to world space
    const worldMatrix = object.matrixWorld.clone();
    corners.forEach((corner) => corner.applyMatrix4(worldMatrix));

    // Convert to Point2D format
    return corners.map((corner) => ({ x: corner.x, z: corner.z }));
  }
};

/**
 * Gets 3D bounding box for an object
 * @param object Three.js mesh object
 * @returns Object with min and max vectors
 */
export const getBounding = (object: THREE.Mesh) => {
  // For rotated objects, calculate a more precise bounding box
  if (object.rotation.y !== 0) {
    // Get the corners in world space
    const corners2D = getCorners(object);

    // Find the min/max X and Z from corners
    let minX = Infinity,
      maxX = -Infinity,
      minZ = Infinity,
      maxZ = -Infinity;

    corners2D.forEach((corner) => {
      minX = Math.min(minX, corner.x);
      maxX = Math.max(maxX, corner.x);
      minZ = Math.min(minZ, corner.z);
      maxZ = Math.max(maxZ, corner.z);
    });

    // Get Y values from the standard bounding box
    const standardBox = new THREE.Box3().setFromObject(object);

    return {
      min: new THREE.Vector3(minX, standardBox.min.y, minZ),
      max: new THREE.Vector3(maxX, standardBox.max.y, maxZ),
    };
  }

  // For non-rotated objects, use the standard THREE.js bounding box
  const boundingBox = new THREE.Box3().setFromObject(object);
  return {
    min: boundingBox.min.clone(),
    max: boundingBox.max.clone(),
  };
};

/**
 * Generates snap points for an object
 * @param object Three.js mesh object
 * @returns Array of snap points in world space
 */
export const getSnapPoints = (object: THREE.Mesh): Point2D[] => {
  // Get the object's corners as snap points
  const corners = getCorners(object);

  // For objects with custom snap points defined, use those instead
  if (object.userData.snapPoints) {
    return object.userData.snapPoints;
  }

  // Add center points of each edge as additional snap points
  const edgeCenters: Point2D[] = [];
  for (let i = 0; i < corners.length; i++) {
    const nextIndex = (i + 1) % corners.length;
    edgeCenters.push({
      x: (corners[i].x + corners[nextIndex].x) / 2,
      z: (corners[i].z + corners[nextIndex].z) / 2,
    });
  }

  // Combine corners and edge centers
  return [...corners, ...edgeCenters];
};

/**
 * Visualize collision areas between objects (for debugging)
 * @param scene The Three.js scene
 * @param object1 First object to check
 * @param object2 Second object to check
 * @returns {THREE.Mesh|null} The visualization mesh or null if no collision
 */
export const visualizeCollision = (
  scene: THREE.Scene,
  object1: THREE.Mesh,
  object2: THREE.Mesh
): THREE.Mesh | null => {
  const corners1 = getCorners(object1);
  const corners2 = getCorners(object2);

  const isColliding = polygonPolygonIntersect(corners1, corners2);

  // Remove any existing collision visualization
  scene.children.forEach((child) => {
    if (child.userData.isCollisionVisualization) {
      scene.remove(child);
    }
  });

  if (!isColliding) {
    return null;
  }

  // Create a visualization for the collision
  // This is simplified - a better visualization would calculate the actual intersection area
  const bbox1 = getBoundingBox2D(corners1);
  const bbox2 = getBoundingBox2D(corners2);

  // Find the intersection of bounding boxes
  const minX = Math.max(bbox1.minX, bbox2.minX);
  const maxX = Math.min(bbox1.maxX, bbox2.maxX);
  const minZ = Math.max(bbox1.minZ, bbox2.minZ);
  const maxZ = Math.min(bbox1.maxZ, bbox2.maxZ);

  // Create a plane to visualize the collision
  const width = maxX - minX;
  const depth = maxZ - minZ;
  const geometry = new THREE.PlaneGeometry(width, depth);
  const material = new THREE.MeshBasicMaterial({
    color: 0xff0000,
    transparent: true,
    opacity: 0.5,
    side: THREE.DoubleSide,
  });
  const plane = new THREE.Mesh(geometry, material);

  // Position the plane at the center of the intersection
  plane.position.set(
    minX + width / 2,
    0.01, // Slightly above the floor to prevent z-fighting
    minZ + depth / 2
  );
  plane.rotation.x = -Math.PI / 2; // Rotate to be horizontal
  plane.userData.isCollisionVisualization = true;

  scene.add(plane);
  return plane;
};

/**
 * Creates a wireframe visualization of an object's bounding box
 * @param scene The Three.js scene
 * @param object The object to visualize bounding box for
 * @param color Optional color for the wireframe (default: yellow)
 * @returns The created wireframe object
 */
export const visualizeBoundingBox = (
  scene: THREE.Scene,
  object: THREE.Mesh,
  color = 0xffff00
): THREE.LineSegments => {
  // Remove any existing bounding box visualization for this object
  scene.children.forEach((child) => {
    if (
      child.userData.isBoundingBoxVisualization &&
      child.userData.targetObjectId === object.uuid
    ) {
      scene.remove(child);
    }
  });

  // Get the bounding box
  const bounds = getBounding(object);

  // Create a box geometry based on the bounds
  const width = bounds.max.x - bounds.min.x;
  const height = bounds.max.y - bounds.min.y;
  const depth = bounds.max.z - bounds.min.z;

  const boxGeometry = new THREE.BoxGeometry(width, height, depth);
  const wireframe = new THREE.EdgesGeometry(boxGeometry);

  // Create a wireframe material
  const material = new THREE.LineBasicMaterial({
    color: color,
    linewidth: 1,
  });

  // Create the wireframe mesh
  const wireframeMesh = new THREE.LineSegments(wireframe, material);

  // Position the wireframe at the center of the bounding box
  wireframeMesh.position.set(
    bounds.min.x + width / 2,
    bounds.min.y + height / 2,
    bounds.min.z + depth / 2
  );

  // Set custom properties for identification
  wireframeMesh.userData.isBoundingBoxVisualization = true;
  wireframeMesh.userData.targetObjectId = object.uuid;

  // Add to scene
  scene.add(wireframeMesh);

  return wireframeMesh;
};
